/* EDITORIAL — Template 2 preview (premium / lifestyle, narrative-driven).
 * Sample brand: Gourmet Fine Dining. Aubergine + Champagne. Dark mode. */
import { useCatalogFonts } from "./_fonts";
import { PreviewRibbon } from "./LinesheetPreview";
import { ShoppingCart, ChevronRight, ChevronLeft, Plus, ArrowUpRight, Award } from "lucide-react";

const IMG_HERO   = "https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_BEST   = "https://images.unsplash.com/photo-1676471926534-d5c9771909fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxnb3VybWV0JTIwZm9vZCUyMGZpbmUlMjBkaW5pbmd8ZW58MHx8fHwxNzgxODE4OTg1fDA&ixlib=rb-4.1.0&q=85";
const IMG_ABOUT  = "https://images.unsplash.com/photo-1694021408920-922ff450c525?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwZm9vZCUyMGZpbmUlMjBkaW5pbmd8ZW58MHx8fHwxNzgxODE4OTg1fDA&ixlib=rb-4.1.0&q=85";

const COLORS = {
  bg:       "#2A1B2E",
  surface:  "#3D2B42",
  card:     "#3D2B42",
  primary:  "#E8DCC4",
  text:     "#F7F3EB",
  muted:    "#B5A4BB",
  border:   "rgba(232,220,196,0.14)",
};

const CATEGORIES = [
  { name: "Olive Oils",   img: IMG_HERO },
  { name: "Aged Vinegars",img: IMG_BEST },
  { name: "Saffron & Spice", img: IMG_ABOUT },
  { name: "Truffle",      img: IMG_HERO },
  { name: "Sea Salt",     img: IMG_BEST },
  { name: "Honey",        img: IMG_ABOUT },
];

const BESTSELLERS = [
  { name: "Reserva Estate EVOO 500ml",       price: 28.40, moq: 6,  img: IMG_HERO },
  { name: "7-Year Aged Balsamic",            price: 42.00, moq: 4,  img: IMG_BEST },
  { name: "Sardinian Sea Salt Flakes",       price: 12.80, moq: 12, img: IMG_ABOUT },
  { name: "Persian Saffron Threads 2g",      price: 36.50, moq: 6,  img: IMG_HERO },
  { name: "Black Truffle Carpaccio",         price: 54.00, moq: 4,  img: IMG_BEST },
];

const NEW_ARRIVALS = [
  { name: "Cold-Pressed Avocado Oil",        price: 18.20, moq: 6,  img: IMG_HERO },
  { name: "Smoked Paprika Pimentón",         price: 9.40,  moq: 12, img: IMG_BEST },
  { name: "Limited-Edition Acacia Honey",    price: 22.00, moq: 6,  img: IMG_ABOUT },
  { name: "Black Garlic Confit Jar",         price: 14.80, moq: 8,  img: IMG_HERO },
  { name: "Calabrian Chili Paste",           price: 11.20, moq: 12, img: IMG_BEST },
];

const PRODUCTS = [
  { name: "Reserva Estate EVOO 500ml",     price: 28.40, moq: 6,  img: IMG_HERO,  cat: "Olive Oils" },
  { name: "7-Year Aged Balsamic Vinegar",  price: 42.00, moq: 4,  img: IMG_BEST,  cat: "Aged Vinegars" },
  { name: "Persian Saffron Threads 2g",    price: 36.50, moq: 6,  img: IMG_ABOUT, cat: "Saffron & Spice" },
  { name: "Single Estate Truffle Oil",     price: 32.80, moq: 6,  img: IMG_HERO,  cat: "Truffle" },
  { name: "Sardinian Sea Salt Flakes",     price: 12.80, moq: 12, img: IMG_BEST,  cat: "Sea Salt" },
  { name: "Wildflower Acacia Honey",       price: 19.40, moq: 6,  img: IMG_ABOUT, cat: "Honey" },
];

const PRESS = ["Bon Appétit", "Eater", "Cherry Bombe", "Saveur"];

export default function EditorialPreview() {
  useCatalogFonts();
  return (
    <div style={{ background: COLORS.bg, color: COLORS.text, fontFamily: "'Outfit', sans-serif", minHeight: "100vh" }} data-testid="editorial-preview">
      <PreviewRibbon name="Editorial"/>

      {/* Slim brand bar */}
      <div className="sticky top-9 z-20 px-8 py-5 flex items-center justify-between" style={{ background: COLORS.bg + "f5", borderBottom: `1px solid ${COLORS.border}`, backdropFilter: "blur(8px)" }}>
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full grid place-items-center text-base" style={{ background: COLORS.primary, color: COLORS.bg, fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>L</span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>Larders &amp; Cellars · est. 2018</div>
            <div className="text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Larders Maison &mdash; Fine Goods</div>
          </div>
        </div>
        <button className="h-10 px-5 rounded-full text-sm inline-flex items-center gap-2" style={{ background: COLORS.primary, color: COLORS.bg, fontWeight: 500 }} data-testid="cart-btn">
          <ShoppingCart className="w-4 h-4"/>Cart · 2
        </button>
      </div>

      {/* HERO */}
      <section className="relative" style={{ minHeight: 460 }}>
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="w-full h-full object-cover" style={{ filter: "brightness(0.55)" }}/>
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, #2A1B2Ecc 0%, #2A1B2E55 60%, transparent 100%)" }}/>
        </div>
        <div className="relative max-w-6xl mx-auto px-8 py-24">
          <div className="text-[11px] uppercase tracking-[0.32em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>Winter linesheet · 2026</div>
          <h1 className="mt-5 max-w-3xl tracking-tight leading-[0.96]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(48px, 7vw, 88px)", fontWeight: 600, fontStyle: "italic" }}>
            A pantry, quietly extraordinary.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed" style={{ color: COLORS.muted }}>
            Estate-pressed olive oils, century-method vinegars, hand-harvested salt &mdash;
            sourced from single-origin growers across the Mediterranean and stocked in
            kitchens of the world&apos;s most thoughtful chefs.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button className="h-12 px-7 rounded-full text-sm font-medium inline-flex items-center gap-2" style={{ background: COLORS.primary, color: COLORS.bg }}>
              Shop the collection <ArrowUpRight className="w-4 h-4"/>
            </button>
            <button className="h-12 px-6 rounded-full text-sm inline-flex items-center gap-2" style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text }}>
              View linesheet ·  PDF
            </button>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-6xl mx-auto px-8 py-20">
        <SectionHead overline="Shop by category" title="The pantry, divided"/>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-5">
          {CATEGORIES.map((c) => (
            <button key={c.name} className="group block text-left rounded-xl overflow-hidden relative" style={{ height: 200 }}>
              <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 50%, #2A1B2Edd 100%)" }}/>
              <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 500 }}>{c.name}</div>
                <ChevronRight className="w-4 h-4 opacity-70"/>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* BESTSELLERS HORIZONTAL SCROLL */}
      <section className="max-w-6xl mx-auto px-8 py-12">
        <div className="flex items-end justify-between">
          <SectionHead overline="Bestsellers" title="What kitchens are reaching for"/>
          <div className="flex gap-2">
            <button className="w-9 h-9 rounded-full grid place-items-center" style={{ border: `1px solid ${COLORS.border}` }}><ChevronLeft className="w-4 h-4"/></button>
            <button className="w-9 h-9 rounded-full grid place-items-center" style={{ border: `1px solid ${COLORS.border}` }}><ChevronRight className="w-4 h-4"/></button>
          </div>
        </div>
        <div className="mt-8 flex gap-5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
          {BESTSELLERS.map((p) => <EditorialCard key={p.name} p={p} compact/>)}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-6xl mx-auto px-8 py-12">
        <SectionHead overline="New arrivals · this week" title="Just landed in our cellar"/>
        <div className="mt-8 flex gap-5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
          {NEW_ARRIVALS.map((p) => <EditorialCard key={p.name} p={p} compact/>)}
        </div>
      </section>

      {/* ABOUT BRAND */}
      <section className="max-w-6xl mx-auto px-8 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: "5/6" }}>
            <img src={IMG_ABOUT} alt="" className="w-full h-full object-cover"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>Our story</div>
            <h2 className="mt-5 tracking-tight leading-[1.02]" style={{ fontFamily: "'Playfair Display', serif", fontSize: 52, fontWeight: 500, fontStyle: "italic" }}>
              Seven harvests.<br/>One conviction.
            </h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: COLORS.muted }}>
              We began in 2018 with a single estate in Puglia and a stubborn belief: that
              wholesale clients deserved the same provenance and care as a Michelin tasting menu.
              Today we work with 14 growers across 5 countries &mdash; and every bottle leaves
              our cellar with the harvest date hand-stamped on the label.
            </p>
            <div className="mt-9 flex items-center gap-6 flex-wrap">
              {PRESS.map((p) => (
                <div key={p} className="text-xs uppercase tracking-[0.24em] opacity-60" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <Award className="inline w-3.5 h-3.5 mr-1.5 opacity-60"/>{p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FULL GRID */}
      <section className="max-w-6xl mx-auto px-8 pb-32">
        <SectionHead overline="The full linesheet" title="Every shelf, every label"/>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => <EditorialCard key={p.name} p={p}/>)}
        </div>
      </section>
    </div>
  );
}

function SectionHead({ overline, title }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.32em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>{overline}</div>
      <h2 className="mt-4 tracking-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 500, fontStyle: "italic", maxWidth: 640 }}>
        {title}
      </h2>
    </div>
  );
}

function EditorialCard({ p, compact }) {
  return (
    <div className="group" style={{ minWidth: compact ? 240 : "auto" }}>
      <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: "3/4" }}>
        <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4" style={{ background: "linear-gradient(180deg, transparent 50%, #2A1B2Eee 100%)" }}>
          <button className="w-full h-10 rounded-full text-xs font-medium inline-flex items-center justify-center gap-1.5" style={{ background: COLORS.primary, color: COLORS.bg }}>
            <Plus className="w-3.5 h-3.5"/>Add to order
          </button>
        </div>
        <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider" style={{ background: "#0000004d", backdropFilter: "blur(8px)", color: COLORS.text }}>
          MOQ {p.moq}
        </span>
      </div>
      <div className="mt-3.5 px-1">
        <div className="text-[15px] leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{p.name}</div>
        <div className="mt-1 flex items-baseline justify-between">
          <div className="text-[17px]" style={{ fontFamily: "'Playfair Display', serif", color: COLORS.primary }}>${p.price.toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-wider opacity-55" style={{ fontFamily: "'JetBrains Mono', monospace" }}>per unit</div>
        </div>
      </div>
    </div>
  );
}
